/**
 * Authentication session context. Mirrors `useMedplum()` and
 * `useMedplumProfile()` and adds a one-way `wasAuthenticated` latch that
 * keeps the React tree mounted across the SDK's brief refresh window
 * (~50–200 ms while `clearActiveLogin()` runs inside
 * `MedplumClient.refreshIfExpired`).
 *
 * Lifted out of the previous `App.tsx` so the new `<RequireAuth>` route
 * element can read the latch, and so explicit-logout paths (inactivity,
 * 401, sidebar) have a single `resetSession()` side-channel that clears
 * the latch and lets the gate redirect.
 *
 * @agentic connections
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type { ProfileResource } from "@medplum/core";

/** Shape of the auth session exposed to consumers. @agentic connections */
export type AuthSession = {
  /** Current Medplum profile, or undefined while loading or signed out. */
  profile: ProfileResource | undefined;
  /** True while the SDK is resolving initial auth or a refresh. */
  isLoading: boolean;
  /**
   * True once a profile has been seen at least once in this tab. Survives
   * the SDK's brief refresh window in which `profile` and `isLoading` may
   * both transiently report unauthenticated state.
   */
  wasAuthenticated: boolean;
  /**
   * Imperative reset for explicit-logout paths (inactivity, SDK 401,
   * sidebar logout). Clears `wasAuthenticated` so `<RequireAuth>` will
   * redirect on the next render.
   */
  resetSession: () => void;
};

/** React context for the auth session. @agentic connections */
const AuthSessionContext = createContext<AuthSession | null>(null);

/**
 * Reads the auth session. Throws if invoked outside `<AuthSessionProvider>`.
 * @agentic connections
 */
export const useAuthSession = (): AuthSession => {
  const value = useContext(AuthSessionContext);
  if (!value) {
    throw new Error(
      "useAuthSession must be used within an AuthSessionProvider",
    );
  }
  return value;
};

/**
 * Provides the auth session to the React tree. Must be mounted inside
 * `<MedplumProvider>` so it can call `useMedplum()` / `useMedplumProfile()`.
 * @agentic connections
 */
export const AuthSessionProvider = ({ children }: { children: ReactNode }) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [wasAuthenticated, setWasAuthenticated] = useState(false);

  // One-way latch: flips true on first profile, never auto-resets.
  // Reset only via the imperative `resetSession()` exposed below.
  if (profile && !wasAuthenticated) {
    setWasAuthenticated(true);
  }

  const resetSession = useCallback(() => {
    setWasAuthenticated(false);
  }, []);

  // Read the loading flag into a value so the memo below depends on the
  // actual boolean, not the stable `medplum` client reference. Depending
  // on `medplum` alone leaves `isLoading` stale when the client finishes
  // resolving auth while `profile`/`wasAuthenticated` are unchanged —
  // stranding the auth gate on `BootSplash`.
  const isLoading = medplum.isLoading();

  const value = useMemo<AuthSession>(
    () => ({
      profile,
      isLoading,
      wasAuthenticated,
      resetSession,
    }),
    [profile, isLoading, wasAuthenticated, resetSession],
  );

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  );
};
